import React, { useEffect, useState } from 'react';
import { faCircleCheck, faPenToSquare, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ApprovalState } from '../../../models/FlowOfFunds/flow-of-funds.model';
import './ApprovalControl.scss';

interface ApprovalControlProps {
    otherPartyApproval: ApprovalState;
    approval: ApprovalState;
    onChange: (next: ApprovalState) => void;
    otherPartyLabel: string;
    viewerLabel: string;
    renderOnly?: 'otherParty' | 'own';
}

export const ReadOnlyApproval = ({ approval }: { approval: ApprovalState }) => {
    if (approval === ApprovalState.Approved) {
        return (
            <span className="ff-approval__status ff-approval__status--approved">
                Approved
            </span>
        );
    }
    if (approval === ApprovalState.Rejected) {
        return (
            <span className="ff-approval__status ff-approval__status--rejected">
                Rejected
            </span>
        );
    }
    return <span className="ff-approval__status">Pending</span>;
};

const OtherPartyCell = ({
    otherPartyApproval,
    otherPartyLabel,
}: Pick<ApprovalControlProps, 'otherPartyApproval' | 'otherPartyLabel'>) => (
    <td className="ff-approval__cell" aria-label={`${otherPartyLabel} approval`}>
        <ReadOnlyApproval approval={otherPartyApproval} />
    </td>
);

const OwnCell = ({
    approval,
    onChange,
    viewerLabel,
}: Pick<ApprovalControlProps, 'approval' | 'onChange' | 'viewerLabel'>) => {
    const [isRevisitingDecision, setIsRevisitingDecision] = useState(false);

    useEffect(() => {
        setIsRevisitingDecision(false);
    }, [approval]);

    const hasDecided =
        approval === ApprovalState.Approved || approval === ApprovalState.Rejected;

    if (hasDecided && !isRevisitingDecision) {
        return (
            <td className="ff-approval__cell" aria-label={`${viewerLabel} approval`}>
                <span className="ff-approval__decided">
                    <ReadOnlyApproval approval={approval} />
                    <button
                        type="button"
                        title="Change your decision"
                        className="ff-approval__edit"
                        onClick={() => setIsRevisitingDecision(true)}>
                        <FontAwesomeIcon icon={faPenToSquare} />
                    </button>
                </span>
            </td>
        );
    }

    return (
        <td className="ff-approval__cell" aria-label={`${viewerLabel} approval`}>
            <span className="ff-approval__buttons">
                <button
                    type="button"
                    title="Approve"
                    className={`ff-approval__btn ff-approval__btn--yes${
                        approval === ApprovalState.Approved ? ' is-selected' : ''
                    }`}
                    onClick={() => onChange(approval === ApprovalState.Approved ? ApprovalState.Pending : ApprovalState.Approved)}>
                    <FontAwesomeIcon icon={faCircleCheck} />
                </button>
                <button
                    type="button"
                    title="Reject"
                    className={`ff-approval__btn ff-approval__btn--no${
                        approval === ApprovalState.Rejected ? ' is-selected' : ''
                    }`}
                    onClick={() => onChange(approval === ApprovalState.Rejected ? ApprovalState.Pending : ApprovalState.Rejected)}>
                    <FontAwesomeIcon icon={faXmark} />
                </button>
            </span>
        </td>
    );
};

export const ApprovalControl = ({
    otherPartyApproval,
    approval,
    onChange,
    otherPartyLabel,
    viewerLabel,
    renderOnly,
}: ApprovalControlProps) => {
    if (renderOnly === 'otherParty') {
        return <OtherPartyCell otherPartyApproval={otherPartyApproval} otherPartyLabel={otherPartyLabel} />;
    }
    if (renderOnly === 'own') {
        return <OwnCell approval={approval} onChange={onChange} viewerLabel={viewerLabel} />;
    }
    return (
        <>
            <OtherPartyCell otherPartyApproval={otherPartyApproval} otherPartyLabel={otherPartyLabel} />
            <OwnCell approval={approval} onChange={onChange} viewerLabel={viewerLabel} />
        </>
    );
};
